import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import PlugIcon from "@mui/icons-material/CableRounded";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { SettingsTabs } from "@/components/settings/settings-tabs";
import { DataTable, type Column } from "@/components/data/data-table";
import { MonoText } from "@/components/display/mono";
import { CopyButton } from "@/components/display/copy-button";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState, LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { SsoProviderDialog } from "@/components/settings/sso-provider-dialog";
import {
  oidcApi,
  samlApi,
  useScimToken,
  useScimMutations,
  useTestSsoProvider,
} from "@/api/settings";

export const Route = createFileRoute("/_app/settings/sso")({
  component: SsoSettings,
});

interface Provider {
  id: string;
  name: string;
  displayName?: string | null;
  enabled?: boolean | null;
  issuerUri?: string | null;
  entityId?: string | null;
}

function SsoSettings() {
  return (
    <PageContainer>
      <PageHeader
        title="Single Sign-On"
        description="Federate authentication with your identity provider and sync users via SCIM."
      />
      <SettingsTabs
        tabs={[
          { value: "oidc", label: "OIDC", render: () => <ProvidersTab kind="oidc" /> },
          { value: "saml", label: "SAML", render: () => <ProvidersTab kind="saml" /> },
          { value: "scim", label: "SCIM provisioning", render: () => <ScimTab /> },
        ]}
      />
    </PageContainer>
  );
}

function ProvidersTab({ kind }: { kind: "oidc" | "saml" }) {
  const toast = useToast();
  const confirm = useConfirm();
  const api = kind === "oidc" ? oidcApi : samlApi;
  const { data, isLoading, error, refetch } = api.useList();
  const m = api.useMutations();
  const test = useTestSsoProvider(kind);
  const [dialog, setDialog] = useState<{ open: boolean; provider: Provider | null }>({
    open: false,
    provider: null,
  });

  const columns: Column<Provider>[] = [
    { id: "name", header: "Name", cell: (r) => r.displayName ?? r.name },
    {
      id: "endpoint",
      header: kind === "oidc" ? "Issuer" : "Entity ID",
      cell: (r) => <MonoText>{r.issuerUri ?? r.entityId ?? "—"}</MonoText>,
    },
    {
      id: "enabled",
      header: "Status",
      cell: (r) =>
        r.enabled ? (
          <Chip size="small" color="success" label="Enabled" />
        ) : (
          <Chip size="small" label="Disabled" />
        ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
          {kind === "oidc" ? (
            <IconButton
              size="small"
              aria-label="Test provider"
              onClick={async (e) => {
                e.stopPropagation();
                const res = await test.mutateAsync(r.id);
                if (res.ok || res.message) toast.success(res.message ?? "Connection OK");
                else toast.error(res.error ?? "Test failed");
              }}
            >
              <PlugIcon fontSize="inherit" />
            </IconButton>
          ) : null}
          <IconButton
            size="small"
            color="error"
            aria-label="Delete provider"
            onClick={async (e) => {
              e.stopPropagation();
              if (
                await confirm({ title: `Delete "${r.displayName ?? r.name}"?`, destructive: true })
              ) {
                await m.remove.mutateAsync(r.id);
                toast.success("Provider deleted");
              }
            }}
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Card>
      <DataTable
        columns={columns}
        data={data as Provider[] | undefined}
        isLoading={isLoading}
        error={error}
        onRetry={() => refetch()}
        rowKey={(r) => r.id}
        toolbar={
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ open: true, provider: null })}
          >
            Add provider
          </Button>
        }
        onRowClick={(r) => setDialog({ open: true, provider: r })}
        empty={
          <EmptyState
            title={`No ${kind.toUpperCase()} providers`}
            description="Add a provider to federate authentication with your identity provider."
          />
        }
      />
      <SsoProviderDialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, provider: null })}
        kind={kind}
        provider={dialog.provider as Record<string, unknown> | null}
      />
    </Card>
  );
}

const scimResultSchema = z.object({ token: z.string().optional() }).passthrough();

function ScimTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = useScimToken();
  const m = useScimMutations();

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2} sx={{ maxWidth: 520 }}>
          <Typography variant="body2" color="text.secondary">
            Point your IdP's SCIM 2.0 connector at <MonoText>/api/scim/v2</MonoText> using a bearer
            token generated here.
          </Typography>
          {data ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Chip size="small" color="success" label="Token active" />
              {data.tokenPrefix ? <MonoText>{data.tokenPrefix}…</MonoText> : null}
              <Typography variant="caption" color="text.secondary">
                created <RelativeTime value={data.createdAt} />
              </Typography>
            </Stack>
          ) : (
            <Chip size="small" label="No token" sx={{ alignSelf: "flex-start" }} />
          )}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={async () => {
                const res = scimResultSchema.parse(await m.generate.mutateAsync());
                if (res.token) {
                  await navigator.clipboard?.writeText(res.token).catch(() => {});
                  toast.success("New SCIM token copied to clipboard — it won't be shown again");
                } else {
                  toast.success("SCIM token generated");
                }
              }}
            >
              {data ? "Regenerate token" : "Generate token"}
            </Button>
            {data ? (
              <Button
                color="error"
                onClick={async () => {
                  if (await confirm({ title: "Revoke SCIM token?", destructive: true })) {
                    await m.revoke.mutateAsync();
                    toast.success("SCIM token revoked");
                  }
                }}
              >
                Revoke
              </Button>
            ) : null}
            <CopyButton value="/api/scim/v2" label="Copy SCIM URL" />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
